// Ubicación en el repo: src/pages/Professionals.jsx
// Administración de Profesionales (spec-prototipo.md 1.5, DECISIONS.md
// #16). Solo Admin/bootstrap llega acá (guard en App.jsx); RLS lo exige
// también del lado del servidor.
import { useCallback, useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  listAllProfessionalsForAdmin,
  softDeleteProfessional,
  restoreProfessional,
} from '../lib/professionals';
import { listActiveBranches } from '../lib/branches';
import ProfessionalFormModal from '../components/ProfessionalFormModal';
import Spinner from '../components/Spinner';

export default function Professionals() {
  const { professional: currentProfessional, selectableBranches } = useOutletContext();
  const [professionals, setProfessionals] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setProfessionals(await listAllProfessionalsForAdmin());
    } catch (err) {
      setError(err.message || 'No se pudo cargar el listado de profesionales.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    listActiveBranches()
      .then(setBranches)
      .catch(() => setBranches(selectableBranches ?? []));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresh]);

  function handleNew() {
    setEditTarget(null);
    setFormOpen(true);
  }

  function handleEdit(p) {
    setEditTarget(p);
    setFormOpen(true);
  }

  async function handleToggleActive(p) {
    if (p.id === currentProfessional.id) {
      window.alert('No puedes desactivar tu propia cuenta.');
      return;
    }
    const action = p.deleted_at ? 'reactivar' : 'desactivar';
    const ok = window.confirm(`¿${action === 'reactivar' ? 'Reactivar' : 'Desactivar'} a ${p.nombre} ${p.apellido}?`);
    if (!ok) return;
    try {
      if (p.deleted_at) {
        await restoreProfessional(p.id);
      } else {
        await softDeleteProfessional(p.id, currentProfessional.id);
      }
      refresh();
    } catch (err) {
      setError(err.message || `No se pudo ${action} al profesional.`);
    }
  }

  function handleFormSaved() {
    setFormOpen(false);
    setEditTarget(null);
    refresh();
  }

  // Guard de UX — RLS (`professionals_insert/update/delete`) es la
  // protección real; esto solo evita que un no-Admin vea una pantalla que
  // de todas formas no puede usar (el link de nav ya está oculto en
  // Layout.jsx, esto cubre a quien llega directo por URL).
  if (!currentProfessional.is_admin) {
    return (
      <div className="patients-page">
        <h1>Profesionales</h1>
        <p className="text-muted">Solo un Admin puede gestionar profesionales.</p>
      </div>
    );
  }

  return (
    <div className="patients-page">
      <div className="patients-page__toolbar">
        <h1>Profesionales</h1>
        <div className="patients-page__toolbar-right">
          <button type="button" className="button-primary" onClick={handleNew}>
            + Nuevo profesional
          </button>
        </div>
      </div>

      {error && <p className="auth-error">{error}</p>}

      <table className="patients-table">
        <thead>
          <tr>
            <th>Nombre</th>
            <th>RUT</th>
            <th>Rol</th>
            <th>Correo</th>
            <th>Sucursales</th>
            <th>Estado</th>
            <th>Acciones</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan={7}><Spinner inline /></td>
            </tr>
          ) : professionals.length === 0 ? (
            <tr>
              <td colSpan={7}>No hay profesionales registrados todavía.</td>
            </tr>
          ) : (
            professionals.map((p) => (
              <tr key={p.id}>
                <td>
                  <div>
                    {p.nombre} {p.apellido}
                    {p.is_admin ? ' · Admin' : ''}
                  </div>
                  {!p.auth_user_id && <div className="text-muted">Invitación pendiente</div>}
                </td>
                <td>{p.rut || '—'}</td>
                <td>{p.role}</td>
                <td>{p.email || '—'}</td>
                <td>
                  {p.branch_ids.length === 0
                    ? 'Todas'
                    : branches
                        .filter((b) => p.branch_ids.includes(b.id))
                        .map((b) => b.name)
                        .join(', ') || `${p.branch_ids.length} asignada(s)`}
                </td>
                <td>
                  {p.deleted_at ? (
                    <span className="badge badge-danger">Inactivo</span>
                  ) : (
                    <span className="badge badge-ok">Activo</span>
                  )}
                </td>
                <td className="actions-cell">
                  <button type="button" className="link-button" onClick={() => handleEdit(p)}>
                    Editar
                  </button>
                  <button
                    type="button"
                    className={`link-button ${p.deleted_at ? '' : 'link-button--danger'}`}
                    onClick={() => handleToggleActive(p)}
                  >
                    {p.deleted_at ? 'Reactivar' : 'Desactivar'}
                  </button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      {formOpen && (
        <ProfessionalFormModal
          professional={editTarget}
          branches={branches}
          restricted={false}
          onClose={() => {
            setFormOpen(false);
            setEditTarget(null);
          }}
          onSaved={handleFormSaved}
        />
      )}
    </div>
  );
}
